import React, { useState, useEffect, useRef, useMemo } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import { modalConfirm, modalQuestionResolution, modalHint, modalHintShow } from "../../utils/logger";
import { buildQuizFeedbackBlockHtml } from "../../utils/quizFeedbackHtml";
import { IconRestore, IconHandMove } from "@tabler/icons-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-blue.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaBlue from "../../assets/btnPistaBlue.png";
import imageHint from "../../assets/hints/imageHintBlue.png";
import imageHintUsed from "../../assets/btnPistaBlue.png";
import unIntento from "../../assets/un-intento-blue.png";
import dosIntentos from "../../assets/dos-intentos-blue.png";

const ACCENT = "#69D0FF";
const ACCENT_BG = "#69D0FF44";

const ITEMS = [
	{ id: "titan", label: "TITAN" },
	{ id: "arches", label: "ARCHES" },
	{ id: "latitude", label: "LATITUDE" },
	{ id: "bajo-grado", label: "BAJO GRADO" },
	{ id: "alto-volumen", label: "ALTO VOLUMEN" },
	{ id: "bajo-volumen", label: "BAJO VOLUMEN" },
	{ id: "bajo-riesgo", label: "BAJO RIESGO" },
	{ id: "alto-riesgo", label: "ALTO RIESGO" },
	{ id: "alto-grado", label: "ALTO GRADO" },
];

function shuffleIds(ids) {
	const copy = [...ids];
	for (let i = copy.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[copy[i], copy[j]] = [copy[j], copy[i]];
	}
	return copy;
}

function Scenario4Quiz2({ intl }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	/* Ajustar en constants/BBDD si esta pregunta tiene otro quizId */
	const QUIZ_ID = QUIZZES.QUIZ8;
	const SCENARIO_ID = SCENARIOS.SCENARIO_4;

	const NUMBERATTEMPTS = 2;
	const CORRECT_ANSWERS = ["TITAN", "ALTO VOLUMEN", "BAJO VOLUMEN"];
	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -5;
	const CORRECT_ANSWER_POINTS_2 = 3;
	const WRONG_ANSWER_POINTS_2 = 0;

	const [slots, setSlots] = useState([null, null, null]);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [attempt, setAttempt] = useState(1);
	const [pendingScore, setPendingScore] = useState(0);
	/** Remonta DnD cuando el pool recupera todas las fichas: evita solapamientos por estado/transforms residuales */
	const [dndSessionKey, setDndSessionKey] = useState(0);
	const prevSlotsOccupiedRef = useRef(0);

	/** Orden aleatorio fijo por carga de página (varía entre grupos / sesiones) */
	const poolDisplayOrder = useMemo(() => shuffleIds(ITEMS.map((it) => it.id)), []);

	const poolItemsInOrder = useMemo(() => {
		const rank = (id) => {
			const i = poolDisplayOrder.indexOf(id);
			return i === -1 ? 999 : i;
		};
		return ITEMS.filter((it) => !slots.includes(it.id)).sort((a, b) => rank(a.id) - rank(b.id));
	}, [slots, poolDisplayOrder]);

	useEffect(() => {
		const occupied = slots.filter(Boolean).length;
		if (prevSlotsOccupiedRef.current > 0 && occupied === 0) {
			setDndSessionKey((k) => k + 1);
		}
		prevSlotsOccupiedRef.current = occupied;
	}, [slots]);

	async function submitAnswer(finalScore, correctFinal) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: finalScore,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO4_QUIZ3,
				correct: correctFinal,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch({ type: "GROUP_SET", payload: serverGroup });
				}
				setIsCompleted(true);
				navigate(ROUTES.SCENARIO4_QUIZ3);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
				});
				navigate(ROUTES.SCENARIO4_QUIZ3);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
			});
			setIsSubmitting(false);
		}
	}

	function resetValues() {
		setSlots([null, null, null]);
	}

	function returnItemToPool(slotIndex) {
		setSlots((prev) => {
			const next = [...prev];
			next[slotIndex] = null;
			return next;
		});
	}

	const retroalimentacion =
		"El estudio TITAN incluyó una población muy representativa de la práctica clínica real, con beneficio tanto en pacientes de alto como de bajo volumen.";
	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(retroalimentacion, "blue");

	const handleHint = async () => {
		const hint =
			"Identifica el estudio que utiliza el concepto de volumen (no riesgo), asignando el 62% a la carga tumoral más alta y el 38% a la más baja.";
		if (hintUsed) {
			await modalHintShow({ hint, imageHintUsed, color: "blue", big: false });
			return;
		}
		const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "blue", big: false });
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(3);
		}
	};

	function checkAnswer() {
		if (slots.includes(null)) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<img src=${estasseguro} alt="Deben completar los espacios" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 blue">Deben completar los tres espacios de la frase</div>`,
			});
			return;
		}

		const labelsInSlots = slots.map((id) => ITEMS.find((it) => it.id === id)?.label);
		const isCorrect =
			labelsInSlots[0] === CORRECT_ANSWERS[0] &&
			labelsInSlots[1] === CORRECT_ANSWERS[1] &&
			labelsInSlots[2] === CORRECT_ANSWERS[2];

		modalConfirm({ estasseguro, color: "blue" }).then((res) => {
			if (!res.isConfirmed) return;

			setIsSubmitting(true);
			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;

			if (isCorrect) {
				const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
				const attemptDelta = base - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintThisAttempt) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: true,
					question: "Población del estudio TITAN",
					textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:420px' />
                     <div class="txt28 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                     ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "blue",
				}).then(() => {
					submitAnswer(newScore, true);
				});
				return;
			}

			if (attempt < NUMBERATTEMPTS) {
				const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintThisAttempt) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: false,
					question: "Población del estudio TITAN",
					textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                     <div class="txt28 bold pt-3">La combinación no es correcta. ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Les queda <b>1</b> intento. Revisen su respuesta y vuelvan a intentarlo.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
					customConfirmButtonHtml: "Intentar de nuevo",
					color: "blue",
				}).then(() => {
					setPendingScore(newScore);
					setAttempt(attempt + 1);
					setIsSubmitting(false);
				});
				return;
			}

			const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt;
			const newScore = pendingScore + attemptDelta;
			if (hintThisAttempt) setHintAppliedToScore(true);

			modalQuestionResolution({
				correct: false,
				question: "Población del estudio TITAN",
				textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                   <div class="txt28 bold pt-4">Han agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                   ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "blue",
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	const onDragEnd = (result) => {
		const { source, destination, draggableId } = result;
		if (!destination) return;

		const newSlots = [...slots];

		if (source.droppableId === "pool" && destination.droppableId.startsWith("slot")) {
			const slotIndex = parseInt(destination.droppableId.replace("slot-", ""), 10);
			newSlots[slotIndex] = draggableId;
		}

		if (source.droppableId.startsWith("slot") && destination.droppableId === "pool") {
			const slotIndex = parseInt(source.droppableId.replace("slot-", ""), 10);
			newSlots[slotIndex] = null;
		}

		if (source.droppableId.startsWith("slot") && destination.droppableId.startsWith("slot")) {
			const fromIndex = parseInt(source.droppableId.replace("slot-", ""), 10);
			const toIndex = parseInt(destination.droppableId.replace("slot-", ""), 10);
			newSlots[toIndex] = draggableId;
			newSlots[fromIndex] = null;
		}

		setSlots(newSlots);
	};

	const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

	const slotStyle = {
		borderBottom: "2px dashed #999",
		minWidth: "48px",
		display: "inline-block",
		margin: "0 4px",
		minHeight: "30px",
		verticalAlign: "baseline",
	};

	return (
		<div className="h-100">
			<div className="container-fluid h-100 px-5 d-flex flex-column justify-content-start pt-5 bgCircleBlue">
				<h3 className="txt24 txt-white text-center" style={{ backgroundColor: "#69D0FF", borderRadius: "10px", padding: "20px"}}>
				Para validar la solidez de la evidencia que sustenta el tratamiento de Santiago, completen el siguiente reporte sobre la población del estudio clínico de referencia:
				</h3>

				<DragDropContext key={dndSessionKey} onDragEnd={onDragEnd}>
					<div className="row">
						<div className="col-12">
							<h3 className="mb-4 txt20 text-left my-2 gray">
								<IconHandMove size={22} color="#757575" stroke={1} /> A partir de la evidencia anterior completen la siguiente frase arrastrando las palabras correspondientes:
							</h3>
							<p className="txt22" style={{ lineHeight: 1.5 }}>
								En el estudio{" "}
								<Droppable droppableId="slot-0">
									{(provided) => (
										<span
											ref={provided.innerRef}
											{...provided.droppableProps}
											style={slotStyle}
										>
											{slots[0] ? (
												<Draggable draggableId={slots[0]} index={0}>
													{(prov) => {
														const item = ITEMS.find((it) => it.id === slots[0]);
														const dh = prov.dragHandleProps;
														return (
															<span
																ref={prov.innerRef}
																{...prov.draggableProps}
																{...dh}
																className="px-2 py-1 rounded text-white cursor-pointer"
																style={{ backgroundColor: ACCENT }}
																onClick={(e) => {
																	dh.onClick?.(e);
																	returnItemToPool(0);
																}}
																title="Clic para devolver a la zona de etiquetas"
															>
																{item?.label}
															</span>
														);
													}}
												</Draggable>
											) : (
												<span className="txt16 gray">_______</span>
											)}
											{provided.placeholder}
										</span>
									)}
								</Droppable>
								, el <strong>62%</strong> de los pacientes incluidos presentaba enfermedad de{" "}
								<Droppable droppableId="slot-1">
									{(provided) => (
										<span
											ref={provided.innerRef}
											{...provided.droppableProps}
											style={slotStyle}
										>
											{slots[1] ? (
												<Draggable draggableId={slots[1]} index={1}>
													{(prov) => {
														const item = ITEMS.find((it) => it.id === slots[1]);
														const dh = prov.dragHandleProps;
														return (
															<span
																ref={prov.innerRef}
																{...prov.draggableProps}
																{...dh}
																className="px-2 py-1 rounded text-white cursor-pointer"
																style={{ backgroundColor: ACCENT }}
																onClick={(e) => {
																	dh.onClick?.(e);
																	returnItemToPool(1);
																}}
																title="Clic para devolver a la zona de etiquetas"
															>
																{item?.label}
															</span>
														);
													}}
												</Draggable>
											) : (
												<span className="txt16 gray">_______</span>
											)}
											{provided.placeholder}
										</span>
									)}
								</Droppable>
								, mientras que el <strong>38%</strong> presentaba enfermedad de{" "}
								<Droppable droppableId="slot-2">
									{(provided) => (
										<span
											ref={provided.innerRef}
											{...provided.droppableProps}
											style={slotStyle}
										>
											{slots[2] ? (
												<Draggable draggableId={slots[2]} index={2}>
													{(prov) => {
														const item = ITEMS.find((it) => it.id === slots[2]);
														const dh = prov.dragHandleProps;
														return (
															<span
																ref={prov.innerRef}
																{...prov.draggableProps}
																{...dh}
																className="px-2 py-1 rounded text-white cursor-pointer"
																style={{ backgroundColor: ACCENT }}
																onClick={(e) => {
																	dh.onClick?.(e);
																	returnItemToPool(2);
																}}
																title="Clic para devolver a la zona de etiquetas"
															>
																{item?.label}
															</span>
														);
													}}
												</Draggable>
											) : (
												<span className="txt16 gray">_______</span>
											)}
											{provided.placeholder}
										</span>
									)}
								</Droppable>
								.
							</p>
						</div>
					</div>

					<div className="row">
						<div className="col-12">
							<Droppable droppableId="pool" direction="horizontal">
								{(provided) => (
									<div
										className="position-relative d-flex justify-content-between mt-2"
										style={{ backgroundColor: ACCENT_BG, borderRadius: "10px", padding: "8px" }}
										{...provided.droppableProps}
										ref={provided.innerRef}
									>
										<div className="d-flex align-items-start flex-wrap flex-grow-1">
											{poolItemsInOrder.map((item, index) => (
													<Draggable key={item.id} draggableId={item.id} index={index}>
														{(prov) => (
															<div
																className="mx-2 px-3 py-1 my-1 rounded shadow-sm txt16 bold cursor-pointer text-center"
																style={{ backgroundColor: ACCENT, color: "white", minWidth: "fit-content" }}
																ref={prov.innerRef}
																{...prov.draggableProps}
																{...prov.dragHandleProps}
															>
																{item.label}
															</div>
														)}
													</Draggable>
												))}
											{provided.placeholder}
										</div>

										<button
											type="button"
											className="btn-main bold shadow bgBlue mt-1 me-1 flex-shrink-0"
											onClick={resetValues}
											style={{ minHeight: "60px", minWidth: "60px" }}
											aria-label="Reiniciar respuestas"
										>
											<IconRestore size={30} color="white" stroke={2} />
										</button>
									</div>
								)}
							</Droppable>
						</div>
						<div className="col-12">
							<div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
								<div className="d-flex justify-content-start mb-1">
									<img src={remainingAttempts === 2 ? dosIntentos : unIntento} className="imgIntentos" alt="" />
								</div>
								<h5 className="mb-0 txt18 text-end blue" style={{ maxWidth: "720px" }}>
									<IconHandMove size={20} color={ACCENT} stroke={1} /> Mantengan presionado la etiqueta unos
									instantes para moverla al hueco correcto en la frase.
									<br />
									Para quitar una opción de un hueco, hagan clic en la etiqueta del hueco, arrástrenla de
									vuelta a la zona de etiquetas o usen el botón Reset{" "}
									<IconRestore size={20} color={ACCENT} stroke={2} />.
								</h5>
							</div>
						</div>
					</div>
				</DragDropContext>

				<div className="d-flex justify-content-between mt-3 mb-2">
					<div className="position-relative d-inline-block">
						<img
							src={btnPistaBlue}
							className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
							onClick={handleHint}
							onKeyDown={(e) => {
								if (e.key === "Enter") void handleHint();
							}}
							alt={hintUsed ? "Pista usada" : "Usar pista"}
							title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
							role="button"
							tabIndex={0}
						/>
						{hintUsed && <span className="badge-penalty">-3</span>}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="blue"
						loading={isSubmitting}
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default injectIntl(Scenario4Quiz2);
